import { CouponData, Product, Wrapping, Cover, Specs } from './interfaces';
import { wrappingDefault, wrappings } from './static';
import { FormGroup, AbstractControl } from '@angular/forms';
import { Logger } from './services/logger.service';
import { getBookByAlias } from './data/books/utils';
import memoizee from 'memoizee';
import { TBookAlias, TBookCover, TBookWrapping, TGender, TGenderGrandpaGrandma, TPlanet } from '@shared/book/interfaces';
import { CalculatorComponentItem } from './shared/calculator/calculator.component';
import { IBookPrice, TBookPrices } from './data/books/prices/types';
import { getCurrentShipping } from './data/shipping/utils';
import { IShipping } from './data/shipping/types';
import { SHIPPING_PREMIUM_NAME, SHIPPING_REGULAR_NAME } from './data/shipping/constants';
import {
  BOOK_ALIAS_STARRY_DREAM,
  BOOK_ALIAS_WELCOME_TO_THE_WOLD,
  BOOK_ALIAS_WHOS_BIRTHDAY_TOMORROW,
  BOOK_ALIAS_WHERE_IS_BIRTHDAY_CAKE,
  BOOK_ALIAS_STORY_OF_GRANDPA_GRANDMA,
} from '@shared/book/constants';
import moment from 'moment';
import jpJP from '@shared/translations/jp-JP.json';
import { default as StripeBE } from 'stripe';
import { calculateDiscount, getBooksDiscount, getShippingDiscount } from './services/discount.service/discount.functions';
import { IDiscountCampaign } from './services/discount.service/discount.interfaces';
import { IFormDataShippingAddress } from './pages/order/interfaces';
import Regions from './data/regions';
import { IFormCustomerInfoData } from './shared/form-customer-info/form-customer-info.interfaces';
import { IPaymentInformation, BookInOrder, ShippingMethod, IAddressBillingData, IAddressShippingData } from '@functions/interfaces';
import {
  BookData,
  BookWelcomeToTheWoldData,
  BookWhosBirthdayTomorrowData,
  BookWhereIsBirthdayCakeData, BookStoryOfGrandpaGrandmaData,
} from '@functions/models';
import { PLANET_TO_NAME_MAP, HAIR_STYLE_WIBC_TO_NAME_MAP, SKIN_COLOR_SOGG_TO_LOCALE_KEY_MAP, HAIR_COLOR_SOGG_TO_LOCALE_KEY_MAP, GENDER_TO_HAIR_STYLE_SOGG_TO_LOCALE_KEY_MAP, BEARD_STYLE_SOGG_TO_LOCALE_KEY_MAP } from '@shared/book/maps';

const FEToBEWrappingNameLog = new Logger('FEToBEWrappingName');
export const FEToBEWrappingName = (
  name: Wrapping['name']
): BookInOrder['wrapping'] => {
  const log = FEToBEWrappingNameLog;

  switch (name) {
    case 'No':
      return 'standart';
    case 'Gift Wrapping':
      return 'gift';
    default:
      log.error(`didn't recognize ${name}`);
      return 'standart';
  }
}

const BEToFEWrappingNameLog = new Logger('BEToFEWrappingName');
export const BEToFEWrappingName = (
  name: BookInOrder['wrapping']
): Wrapping['name'] => {
  const log = BEToFEWrappingNameLog;

  switch (name) {
    case 'standart':
      return 'No';
    case 'gift':
      return 'Gift Wrapping';
    default:
      log.error(`didn't recognize ${name}`);
      return 'No';
  }
}

export const createBookInOrderObject = (
  alias: TBookAlias,
  bookId: string,
  wrapping: TBookWrapping,
  cover: TBookCover,
): BookInOrder => {
  return {
    alias,
    bookId,
    status: 0,
    wrapping,
    cover,
  }
}

export const FEToBEProduct = (
  { alias, bookId, wrapping, cover }: Product
): BookInOrder => {
  return createBookInOrderObject(
    alias,
    bookId,
    FEToBEWrappingName(wrapping.name),
    cover.name,
  );
}

const FEToBEShippingLog = new Logger('FEToBEShipping')
export const FEToBEShipping = (
  shipping: IShipping
): ShippingMethod => {
  let BEShipping: ShippingMethod

  switch (shipping.name) {
    case SHIPPING_PREMIUM_NAME:
      BEShipping = 'premiumShipping';
      break;

    case SHIPPING_REGULAR_NAME:
      BEShipping = 'regularShipping'
      break;

    default:
      FEToBEShippingLog.error('Can`t recognize', { shipping }, 'setting regularShipping by default');
      BEShipping = 'regularShipping';
      break;
  }

  return BEShipping
}

export const isBookStarryDreamData = (
  bookData: any
): bookData is BookData => bookData.alias === BOOK_ALIAS_STARRY_DREAM;

export const isBookWelcomeToTheWoldData = (
  bookData: any
): bookData is BookWelcomeToTheWoldData => bookData.alias === BOOK_ALIAS_WELCOME_TO_THE_WOLD;

export const isBookWhosBirthdayTomorrowData = (
  bookData: any
): bookData is BookWhosBirthdayTomorrowData => bookData.alias === BOOK_ALIAS_WHOS_BIRTHDAY_TOMORROW;

export const isBookWhereIsBirthdayCakeData = (
  bookData: any
): bookData is BookWhereIsBirthdayCakeData => bookData.alias === BOOK_ALIAS_WHERE_IS_BIRTHDAY_CAKE;

export const isBookStoryOfGrandpaGrandmaData = (
  bookData: any
): bookData is BookStoryOfGrandpaGrandmaData => bookData.alias === BOOK_ALIAS_STORY_OF_GRANDPA_GRANDMA;

export const getPhotoText = (
  fileUploadStatus: BookData['fileUploadStatus'],
  photoTransformData: BookData['photoTransformData'],
) => {
  let photoText = '';

  if (fileUploadStatus > 1) {
    photoText = '写真あり' + `・フィルター ${(photoTransformData.opacity * 100).toFixed(0)}%`;
  } else {
    photoText = '写真なし';
  }

  return photoText;
}

export const getPlanetsText = (
  planets: TPlanet[],
) => planets.reduce((str, planet) => str += (str ? '・' : '') + PLANET_TO_NAME_MAP[planet], '');

const getBookDataSpecsLog = new Logger('getBookDataSpecs');

export const getBookDataSpecs = (
  bookData: BookData | BookWelcomeToTheWoldData | BookWhosBirthdayTomorrowData | BookWhereIsBirthdayCakeData | BookStoryOfGrandpaGrandmaData,
  exceptFields: string[] = [],
): Specs => {
  const log = getBookDataSpecsLog;

  const {
    heroName,
    cover,
    messageText,
  } = bookData;

  const specs: Specs = {
    /** cover */
    表紙の種類: cover ? `「${jpJP[cover]}」` : '',
    /** hero's name */
    名前: heroName,
  };

  if (isBookStarryDreamData(bookData)) {
    const {
      birthDate,
      birthDateTimeZone,
      gender,
      zodiacSigns,
      fileUploadStatus,
      photoTransformData,
      planets,
      place,
      birthTime,
    } = bookData;

    Object.assign(specs, {
      /** birthday */
      誕生日: moment(birthDate.toDate()).tz(birthDateTimeZone).format('YYYY/MM/DD'),
      /** gender */
      性別: jpJP[gender],
      /** horoscope */
      星座: jpJP[zodiacSigns],
      /** photo */
      写真: getPhotoText(fileUploadStatus, photoTransformData),
      /** planets to visit */
      行先の星々: getPlanetsText(planets),
      /** birth location */
      出生地: place?.formatted_address || '入力なし',
      /** birth time */
      出生時間: birthTime || '入力なし',
      /** dedication */
      メッセージ: messageText,
    })
  } else if (isBookWelcomeToTheWoldData(bookData)) {
    const {
      birthDate,
      birthDateTimeZone,
      hairStyle,
      parents,
      birthTime,
      timeOfTheYear,
    } = bookData;

    Object.assign(specs, {
      /** birthday */
      誕生日: moment(birthDate.toDate()).tz(birthDateTimeZone).format('YYYY/MM/DD'),
      /** hair style */
      髪型: jpJP[hairStyle],
      /** parents combination */
      親の構成: jpJP[parents],
      /** birth time */
      出生時間: birthTime || '入力なし',
      /** season */
      季節: jpJP[timeOfTheYear],
      /** photo */
      写真: '写真あり',
      /** dedication */
      メッセージ: messageText,
    })
  } else if (isBookWhosBirthdayTomorrowData(bookData)) {
    const {
      birthDate,
      birthMonth,
      gender,
      ageToBe,
      whosCelebrating,
      timeOfTheYear,
      fileUploadStatus,
    } = bookData;

    Object.assign(specs, {
      /** birthday */
      誕生日: `${birthMonth}月 ${birthDate}日`,
      /** gender */
      性別: jpJP[gender],
      /** age to be */
      もうすぐ: `${ageToBe}歳`,
      /** friends */
      お友達: jpJP[whosCelebrating],
      /** season */
      季節: jpJP[timeOfTheYear],
      /** photo */
      写真: (fileUploadStatus > 1) ? '写真あり' : '写真なし',
      /** dedication */
      メッセージ: messageText,
    })
  } else if (isBookWhereIsBirthdayCakeData(bookData)) {
    const {
      birthDate,
      birthMonth,
      gender,
      age,
      hairStyle,
      glasses,
      timeOfTheYear,
      fileUploadStatus,
    } = bookData;

    Object.assign(specs, {
      /** character */
      性別: `${jpJP[gender]} (${HAIR_STYLE_WIBC_TO_NAME_MAP[hairStyle]})`,
      /** glasses */
      メガネ: glasses ? 'あり' : 'なし',
      /** birthday */
      お誕生日: `${birthMonth}月 ${birthDate}日`,
      /** age to be */
      もうすぐ: `${age}歳`,
      /** season */
      季節のキャラクター: jpJP[timeOfTheYear],
      /** photo */
      写真: (fileUploadStatus > 1) ? '写真あり' : '写真なし',
      /** dedication */
      メッセージ: messageText,
    })
  } else if (isBookStoryOfGrandpaGrandmaData(bookData)) {
    const {
      heroName,
      gender,
      skinColor,
      hairColor,
      hairStyle,
      beardStyle,
      glasses,
      hairColorInYoungerYears,
      grandchildren: { howMany, data: [child1, child2, child3] },
      messageText,
    } = bookData;

    delete specs['名前'];

    Object.assign(specs, {
      [jpJP[gender] + 'の名前']: heroName,
      性別: jpJP[gender],
      肌色: jpJP[SKIN_COLOR_SOGG_TO_LOCALE_KEY_MAP[skinColor]],
      髪色: jpJP[HAIR_COLOR_SOGG_TO_LOCALE_KEY_MAP[hairColor]],
      髪型: jpJP[GENDER_TO_HAIR_STYLE_SOGG_TO_LOCALE_KEY_MAP[gender][hairStyle]],
      ひげ: jpJP[BEARD_STYLE_SOGG_TO_LOCALE_KEY_MAP[beardStyle]],
      メガネ: glasses ? 'あり' : 'なし',
      子どもの頃の髪色: jpJP[HAIR_COLOR_SOGG_TO_LOCALE_KEY_MAP[hairColorInYoungerYears]],
      絵本に登場する孫の数: `${howMany}人`,
      孫1の名前: child1?.childrenName,
      孫2の名前: child2?.childrenName,
      孫3の名前: child3?.childrenName,
      写真: 'あり',
      メッセージ: messageText,
    });
  } else {
    log.error(`didn't recognize`, { bookData });
  }

  // removing excepted fields
  exceptFields.forEach(field => delete specs[field]);

  // removing empty fields
  Object.keys(specs).forEach(key => specs[key] || delete specs[key]);

  return specs;
}

const getProductNameLog = new Logger('getProductName');
export const getProductName = (
  alias: TBookAlias,
  heroName: string,
  gender?: TGender | TGenderGrandpaGrandma,
  childrenNames?: string[]
) => {
  const log = getProductNameLog;

  switch (alias) {
    case BOOK_ALIAS_STARRY_DREAM:
      return `${heroName}とほしのゆめ`;
    case BOOK_ALIAS_WELCOME_TO_THE_WOLD:
      return `${heroName}　ようこそ、このせかいへ`;
    case BOOK_ALIAS_WHOS_BIRTHDAY_TOMORROW:
      return `あしたは${heroName}のたんじょうび`;
    case BOOK_ALIAS_WHERE_IS_BIRTHDAY_CAKE:
      return `${heroName}、みつけて！たんじょうびまでのだいぼうけん`;
    case BOOK_ALIAS_STORY_OF_GRANDPA_GRANDMA:
      if (!childrenNames) {
        log.error(BOOK_ALIAS_STORY_OF_GRANDPA_GRANDMA, 'should have childrenNames');
        return ''
      };

      return `${childrenNames.join('と')}の${jpJP[gender]}、${heroName}`;
    default:
      log.error(`alias "${alias}" not handled yet`);
      return '';
  }
}

type TGetProductRequiredFields = 'alias' | 'bookId' | 'hero' | 'specs';

type TGetProductExcludedFields = 'wrappings' | 'name';

type TGetProductFields = Required<
  Pick<Product, TGetProductRequiredFields>
> & Partial<
  Omit<Product, TGetProductRequiredFields | TGetProductExcludedFields>
>;

export const getProduct = (
  {
    alias,
    bookId,
    covers,
    cover,
    hero,
    children,
    gender,
    image,
    wrapping,
    specs,
  }: TGetProductFields
): Product => {
  const book = getBookByAlias(alias);

  const p: Product = {
    alias,
    bookId,
    name: getProductName(alias, hero, gender, children),
    children,
    gender,
    covers,
    cover,
    hero,
    image: image || book.menuPhoto,
    wrappings,
    wrapping: wrapping || wrappingDefault,
    specs,
  }

  p.covers = p.covers || getCovers(p.alias);

  p.cover = p.cover || getCoverSoft(p.alias);

  return p;
}

export const getCovers = memoizee(
  (alias: TBookAlias, prices?: TBookPrices): Cover[] => [
    getCoverSoft(alias, prices),
    getCoverHard(alias, prices),
  ],
);

export const getCover = (alias: TBookAlias, cover: TBookCover) => {
  if (cover === 'hard') {
    return getCoverHard(alias);
  }

  return getCoverSoft(alias);
}

export const getCoverSoft = memoizee(
  (
    alias: TBookAlias,
    prices: TBookPrices = getBookByAlias(alias).prices
  ): Cover => {
    const price = prices.find(_price => _price[0] === 'soft')[1];

    return {
      name: 'soft',
      summary: '小さな子供の手でもめくりやすい、文庫本のような柔らかい表紙です。',
      price,
      image: '/assets/images/soft.png',
    }
  },
);

export const getCoverHard = memoizee(
  (
    alias: TBookAlias,
    prices: TBookPrices = getBookByAlias(alias).prices
  ): Cover => {
    const price = prices.find(_price => _price[0] === 'hard')[1];

    return {
      name: 'hard',
      summary: '丈夫で耐久性のある硬い表紙。ギフトや記念にとっておきたい時はこちらがおすすめ。',
      price,
      image: '/assets/images/hard.png',
    }
  },
);

export const getCalculatorItems = (
  products: Product[],
  discountCampaign?: IDiscountCampaign,
  couponData?: CouponData,
  shipping = getCurrentShipping(products.length),
): CalculatorComponentItem[] => {
  const calculatorItems: CalculatorComponentItem[] = [];

  calculatorItems.push(
    ...products.map(p => {
      let price = p.cover.price.value + p.wrapping.price.value;

      const discount = discountCampaign && getBooksDiscount(discountCampaign, p.alias);

      if (discount) {
        price = calculateDiscount(price, discount);
      }

      if (checkStripeCoupon(couponData)) {
        price = calculateDiscount(price, getCouponDiscount(couponData));
      }

      return {
        name: 'Product',
        price,
        currency: p.cover.price.currency,
      }
    })
  );

  const shippingMethod = FEToBEShipping(shipping);
  let shippingPrice = shipping.price;

  const shippingDiscount = discountCampaign && getShippingDiscount(discountCampaign, shippingMethod);

  if (shippingDiscount) {
    shippingPrice = calculateDiscount(shippingPrice, shippingDiscount);
  }

  calculatorItems.push({
    name: 'Shipping',
    price: shippingPrice,
    currency: shipping.currency,
  });

  return calculatorItems
}

export const getProductPrice = (p: Product): IBookPrice => {
  return {
    value: p.cover.price.value + p.wrapping.price.value,
    currency: p.cover.price.currency,
  }
}

// ! note: do not use margin in el style attr
export const controlPopupPosition = (
  el: HTMLElement,
  offset: number
): void => {
  if (!el) {
    console.error('controlPopupPosition: element is not provided', el)
    return
  }

  const rect: DOMRect = el.getBoundingClientRect()
  const marginLeft    = (el.style.marginLeft ? parseInt(el.style.marginLeft, 10) : 0) * -1
  const rectRight     = rect.x + rect.width + offset + marginLeft

  // check if element crossed the edge
  if (rectRight > window.innerWidth) {
    const gap = rectRight - window.innerWidth

    el.style.marginLeft = `-${gap}px`
  } else {
    el.style.marginLeft = ''
  }
}

export const separateThousandsByComma = (x: number | string): string => {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

export const checkStripeCoupon = (coupon: any): coupon is StripeBE.Coupon => {
  return coupon && ('percent_off' in coupon || 'amount_off' in coupon);
}

export const getCouponDiscount = ({ percent_off, amount_off }: StripeBE.Coupon): string => {
  if (percent_off) {
    return `${percent_off}%`;
  } else if (amount_off) {
    return `${amount_off}`;
  }

  return '0';
}

export const checkControlShowError = (control: AbstractControl): boolean => {
  if (!control) return false;

  const { dirty, touched } = control;

  return control.invalid ? (dirty || touched) : false;
}

export { today } from './today';

export const checkAllControlsTouchedInFormGroup = (
  fg: FormGroup
): boolean => {
  const { controls } = fg;

  return Object.values(controls).every(c => c.touched);
}

/**
 * removes undefined from object and array `recursively`
 * @param obj - [undeifined, 1, 2] => [empty, 1, 2]
 * @param obj - { a: undefined, b: '' } => { b: '' }
 */
export const deleteUndefinedFromObject = <T>(obj: T ) => {
  Object.keys(obj).forEach(key => {
    if (obj[key] === undefined) {
      delete obj[key];
    } else if (obj[key] instanceof Object) {
      deleteUndefinedFromObject(obj[key]);
    }
  });

  return obj;
}

export const formDataCustomerInfoToAddressBilingData = (
  fCI: IFormCustomerInfoData,
): IAddressBillingData => ({
  familyName: fCI.lastName,
  givenName: fCI.firstName,
  postalCode: fCI.postalCodeFirst + '-' + fCI.postalCodeSecond,
  addressLine1: fCI.addressFirst ? Regions[fCI.addressFirst].name : '',
  addressLine2: fCI.addressSecond,
  addressLine3: fCI.addressThird,
  phone: fCI.phoneFirst + '-' + fCI.phoneSecond + '-' + fCI.phoneThird,
  emailAddress: fCI.email,
});

// export const addressBillingDataToFormDataCustomerInfo = (
//   aBD: IAddressBillingData,
// ): IFormCustomerInfoData => {
//   const {
//     familyName: lastName,
//     givenName: firstName,
//     postalCode,
//     addressLine1,
//     addressLine2: addressSecond,
//     addressLine3: addressThird,
//     phone,
//     emailAddress: email,
//   } = aBD;

//   const [postalCodeFirst, postalCodeSecond] = postalCode.split('-');
//   const addressFirst = Regions.findIndex(r => r.name === addressLine1).toString();
//   const [phoneFirst, phoneSecond, phoneThird] = phone.split('-');

//   return {
//     lastName,
//     firstName,
//     postalCodeFirst,
//     postalCodeSecond,
//     addressFirst,
//     addressSecond,
//     addressThird,
//     phoneFirst,
//     phoneSecond,
//     phoneThird,
//     email,
//   }
// };

export const formDataShippingAddressToAddressShippingData = (
  fSA: IFormDataShippingAddress,
): IAddressShippingData => ({
  familyName: fSA.lastName,
  givenName: fSA.firstName,
  postalCode: fSA.postalCodeFirst + '-' + fSA.postalCodeSecond,
  addressLine1: fSA.addressFirst ? Regions[fSA.addressFirst].name : '',
  addressLine2: fSA.addressSecond,
  addressLine3: fSA.addressThird,
  phone: fSA.phoneFirst + '-' + fSA.phoneSecond + '-' + fSA.phoneThird,
});

export const scrollToElement = (
  element: Element,
  container = document.documentElement,
): void => {
  const { x, y } = element.getBoundingClientRect();
  let yGap = 0;

  if (container === document.documentElement) {
    const headerGapElement = document.getElementsByTagName('app-section-header-gap')[0];

    if (headerGapElement) {
      yGap = headerGapElement.getBoundingClientRect().height;
    }
  }

  container.scrollBy({
    top: y - yGap,
    left: x,
    behavior: 'smooth',
  });
}

const scrollToSelectorLog = new Logger('scrollToSelector');
export const scrollToSelector = (
  selector: string,
  container = document.documentElement,
): void => {
  const log = scrollToSelectorLog;
  const element = document.querySelector(selector);

  if (!element) {
    log.error('element not found by selector', selector);
    return;
  }

  scrollToElement(element, container);
}

export const getPaymentInformation = (
  books: {
    alias: TBookAlias;
    cover: TBookCover;
  }[],
  shippingMethod: ShippingMethod,
  shippingPrice: number,
  type: IPaymentInformation['type'],
  couponData?: CouponData,
  discountCampaign?: IDiscountCampaign,
): IPaymentInformation => {
  const shippingDiscount = discountCampaign && getShippingDiscount(discountCampaign, shippingMethod);

  shippingPrice = shippingDiscount
    ? calculateDiscount(shippingPrice, shippingDiscount)
    : shippingPrice;

  /** raw books price */
  const productSubtotal: IPaymentInformation['productSubtotal']
    = books.reduce((price, { alias, cover }) => {
      const bookPrice = getCover(alias, cover).price.value;

      // calculate discount
      let bookPriceWithDiscount = bookPrice;

      const discount = discountCampaign && getBooksDiscount(discountCampaign, alias);

      if (discount) {
        bookPriceWithDiscount = calculateDiscount(bookPriceWithDiscount, discount);
      }

      price += bookPriceWithDiscount;

      return price;
    }, 0);

  /** final books price */
  let productTotal = productSubtotal;
  let couponCode: IPaymentInformation['couponCode'];

  // apply stripe coupon
  if (checkStripeCoupon(couponData)) {
    productTotal = calculateDiscount(productTotal, getCouponDiscount(couponData));
    couponCode = couponData.id;
  }

  return {
    couponCode,
    discountAmount: productSubtotal - productTotal,
    productSubtotal,
    productTotal,
    shippingPrice,
    orderTotal: productTotal + shippingPrice,
    type,
  }
}
